import { getNumberOfDaysBetween, HOURS, IDay, type IHosAvailability, type IState, parseDateTime } from "./helpers";
import { getWorkdayForDateTime, isWorkdayReadyForReset } from "./hosCalculator";
import { hasEmergencyResponseSuppressedViolation, isEmergencyResponseActive } from "./exemptions/emergencyResponse";
import type { TViolationType } from "../interfaces/statues";

export const adverseExtensionAmount = 2 * HOURS;

export const isExemptionAppliedDuringWorkday = (state: IState, exemptionDate: Date) => {
    if (!state.workday.start || !state.workday.end) {
        return false;
    }
    return (
        exemptionDate.getTime() > state.workday.start.getTime() &&
        exemptionDate.getTime() <= state.workday.end.getTime() + (state.config.bigDayBonus || 0)
    );
};

export const isBigDayExemptionReset = (state: IState): boolean => {
    // If multiple big day exemptions per cycle (150 air mile non-cdl SH)
    let maxBigDayPerCycle = state.config.bigDayMaxPerCycle || 1;

    // Count of valid exemptions applied since one day after cycleStart
    if (!state.workday.end) {
        return true;
    }
    return state.cycle.bigDayCount < maxBigDayPerCycle;
};

export const getOffAndRestRequirementReductionForDeferralDay = (day: IDay) => {
    let deferralTime = day.deferralTime ?? 0;
    switch (day.deferralStatus) {
        case "DayOne":
            return deferralTime;
        case "DayTwo":
            return -deferralTime;
        default:
            return 0;
    }
};

export const getAdcExtensionAmountForDay = (day: IDay): number => (day?.isAdverseLogApplied ? adverseExtensionAmount : 0);

// eslint-disable-next-line complexity
export const setExemptionValues = (state: IState, availability: IHosAvailability) => {
    let workdayStarted = state.workday.hasStarted;
    let isOffSbPc = state.next?.status && state.next.status.match(/^(OFF|SB|PC)$/);

    if (state.config.railroadExempt) {
        availability.isRailroadExemptionAvailable =
            workdayStarted && !isWorkdayReadyForReset(state) && state.config.cycle?.cycleMaximum === state.cycle.cycleMaximum;
    }
    if (state.workday.drivingLimit) {
        // Checking if driver is currently in OFF/SB/PC. This is required to prevent button from being available when driver has been OFF duty.
        // Also, if drivingExtended has value - isAdverseDrivingApplied is true.
        availability.isAdverseDrivingExemptionAvailable = !isOffSbPc;
        availability.isAdverseDrivingApplied = state.workday.drivingExtended !== undefined && state.workday.drivingExtended > 0;
    }
    if (isEmergencyResponseActive(state.emergencyResponsePeriods, state.nextDate)) {
        availability.isEmergencyResponseExemptionApplied = true;
    }
    if (state.config.bigDayBonus && state.config.workday?.workdayMaximum) {
        availability.is16HourExemptionAvailable = workdayStarted && !isTooLateToApplyBigDayExemption(state) && isBigDayExemptionReset(state);
    }
    if (state.config.workday?.exemptionReducedRest) {
        let eligibleDurationToReduceRest = getEligibleDurationToReduceRest(state);
        availability.exemptionReducedRest = {
            isAvailable: isExemptionReducedRestAvailable(state, eligibleDurationToReduceRest.restAmountToBeDeferred),
            restAmountToBeDeferred: eligibleDurationToReduceRest.restAmountToBeDeferred
        };
    }
    if (state.config.splitConfig != null) {
        let lastWorkday = state.workdays[state.workdays.length - 2];
        availability.isSplittingWorkday = !!lastWorkday && lastWorkday.completedSplitSB === true;
    }
    if (state.isInit && state.config.rest && state.config.rest.dailyRestMinimum !== undefined && state.config.daily !== undefined) {
        availability.isOffDutyDeferralExemptionAvailable =
            state.config.daily?.deferralAllowed !== false &&
            state.workday.hasStarted &&
            (state.day.deferralStatus === undefined || state.day.deferralStatus === "None" || state.day.deferralTime === undefined);
    }
};

const isExemptionReducedRestAvailable = (state: IState, restAmountToBeDeferred: number): boolean => {
    let hasOngoingOffDuty = false,
        hasERRAppliedAfterOngoingOFFDuty = false;
    if (state.workday.eligibleReducedRestPeriods && state.workday.eligibleReducedRestPeriods.length > 0) {
        let currentPeriod = state.workday.eligibleReducedRestPeriods[state.workday.eligibleReducedRestPeriods.length - 1];
        if (currentPeriod.start && !currentPeriod.end) {
            // This is an ongoing OFF duty period, check if ERR is applied after this ongoing OFF period started
            hasOngoingOffDuty = true;
            hasERRAppliedAfterOngoingOFFDuty = state.reducedRestLogs.some(
                (log: { dateTime: Date }) => currentPeriod.start && log.dateTime > currentPeriod.start
            );
        }
    }
    // MYG-139401: an Emergency Response exemption closes the violation record but leaves the
    // driver's accumulated hours past the limit, so the marker answers for the types whose record
    // it cut short. Reading currentViolations alone would offer Reduced Rest to a driver who is
    // still in violation.
    const disqualifyingViolations: readonly TViolationType[] = ["Workday", "Driving", "Duty", "Rest"];
    let isUserInViolation =
        disqualifyingViolations.some(violationType => violationType in state.currentViolations) ||
        hasEmergencyResponseSuppressedViolation(state, disqualifyingViolations);

    return (
        isExemptionReducedRestReset(state) && // Exemption is available when it is not taken in last 7 days and,
        state.workday.hasStarted && // Workday has started and,
        !isUserInViolation && // User does not have violation and,
        ((hasOngoingOffDuty && !hasERRAppliedAfterOngoingOFFDuty) || // there is an ongoing OFF with no ERR applied after it started or,
            restAmountToBeDeferred > 0) // There is an eligible OFF duty period available > 4H and < 8H in the current workday.
    );
};

const isExemptionReducedRestReset = (state: IState): boolean => {
    if (!state.current || !state.lastReducedRestApplyDate) {
        return true;
    }
    let daysBetweenUse = state.config.workday?.exemptionReducedRest?.daysBetweenUse || 7;
    return getNumberOfDaysBetween(state.current.dateTime, state.lastReducedRestApplyDate, state.toLocalTime) >= daysBetweenUse;
};

const isTooLateToApplyBigDayExemption = (state: IState): boolean => {
    return (
        state.workday.start === undefined ||
        state.config.workday === undefined ||
        state.config.workday.workdayMaximum === undefined ||
        state.config.bigDayBonus === undefined ||
        new Date(state.workday.start.getTime() + state.config.workday.workdayMaximum + state.config.bigDayBonus) < state.nextDate
    );
};

export const getEligibleDurationToReduceRest = (state: IState, exemptionDateTime?: Date) => {
    // Find a OFF log in the range offDutyMin <= logDuration < offDutyMax
    let result: {
        nextWorkdayStart: Date | undefined;
        restAmountToBeDeferred: number;
    } = {
        nextWorkdayStart: undefined,
        restAmountToBeDeferred: 0
    };

    // Find workday during which the exemptionDateTime falls
    let workdayWithExemption = exemptionDateTime ? getWorkdayForDateTime(state, exemptionDateTime) : state.workday;
    if (
        !state.current ||
        !state.config.workday?.offDutyMinimum ||
        !workdayWithExemption?.start ||
        !workdayWithExemption.eligibleReducedRestPeriods ||
        workdayWithExemption.eligibleReducedRestPeriods.length === 0
    ) {
        return result;
    }

    let exemptionStart = exemptionDateTime ?? parseDateTime(state.current.dateTime);
    for (let i = workdayWithExemption.eligibleReducedRestPeriods.length - 1; i >= 0; i--) {
        let period = workdayWithExemption.eligibleReducedRestPeriods[i];
        // Find an eligible period that start before exemption has been applied is in the workdayWithExemption
        if (
            period.start &&
            period.end &&
            period.start.getTime() > workdayWithExemption.start.getTime() &&
            period.start.getTime() <= exemptionStart.getTime()
        ) {
            result.nextWorkdayStart = period.end;
            result.restAmountToBeDeferred = state.config.workday.offDutyMinimum - (period.end.getTime() - period.start.getTime());
            break;
        }
    }
    return result;
};

export interface IWorkdayExtensionParams {
    driving: number;
    workday: number;
    duty: number;
}

export const applyWorkdayExtensions = (state: IState, workdayExtension: number): void => {
    state.workday.end?.setTime(state.workday.end.getTime() + workdayExtension);
    if (state.workday.workdayExtended !== undefined) {
        state.workday.workdayExtended += workdayExtension;
    } else {
        state.workday.workdayExtended = workdayExtension;
    }
};

export const applyDrivingExtensions = (state: IState, drivingExtension: number): void => {
    if (state.workday.drivingLimit !== undefined) {
        state.workday.drivingLimit += drivingExtension;
        state.workday.drivingExtended = drivingExtension;
    }
};

export const applyDutyExtensions = (state: IState, dutyExtension: number): void => {
    if (state.workday.dutyLimit !== undefined) {
        state.workday.dutyLimit += dutyExtension;
    }
};

export interface IWorkdayExemptionParams {
    hasAdverse?: boolean;
    hasBigDay?: boolean;
    adverseDoesNotExtendWorkday?: boolean;
    bigDayExtensionAmount?: number;
}

export const calculateExemptionExtensions = (params: IWorkdayExemptionParams): IWorkdayExtensionParams => {
    const drivingExtension = params.hasAdverse ? adverseExtensionAmount : 0;
    const dutyExtension = params.hasAdverse ? adverseExtensionAmount : 0;

    let workdayExtension = 0;

    if (params.hasBigDay && params.hasAdverse) {
        const adverseWorkdayExtension = params.adverseDoesNotExtendWorkday ? 0 : adverseExtensionAmount;
        const bigDayExtension = params.bigDayExtensionAmount ?? 0;
        const maxExtension = Math.max(adverseWorkdayExtension, bigDayExtension);
        const minExtension = Math.min(adverseWorkdayExtension, bigDayExtension);
        workdayExtension = maxExtension - minExtension;
    } else if (params.hasAdverse && !params.hasBigDay && !params.adverseDoesNotExtendWorkday) {
        workdayExtension = adverseExtensionAmount;
    } else if (params.hasBigDay && !params.hasAdverse) {
        workdayExtension = params.bigDayExtensionAmount ?? 0;
    }

    return {
        driving: drivingExtension,
        workday: workdayExtension,
        duty: dutyExtension
    };
};
